import { post, del, get, put } from "@zendesk/knowledge-http-client";
import { push } from "connected-react-router";
import t from "@zendesk/client-i18n-tools";
import {
  getCSRFToken,
  getCurrentBrandId,
  hasEntitlement,
} from "@zendesk/knowledge-context";
import { STAFF, SIGNED_IN_USERS } from "../../constants";
import * as types from "./types";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../notifications";

const USERS_PER_PAGE = 30;
const USER_SEGMENTS_URL = "/api/v2/help_center/user_segments";

function duplicateUserSegmentRequest(userSegment) {
  return {
    type: types.DUPLICATE_USER_SEGMENT_REQUEST,
    userSegment,
  };
}

function duplicateUserSegmentSuccess(originalId, userSegment) {
  return {
    type: types.DUPLICATE_USER_SEGMENT_SUCCESS,
    userSegment,
    originalId,
  };
}

function duplicateUserSegmentFailure(error) {
  return {
    type: types.DUPLICATE_USER_SEGMENT_FAILURE,
    error: true,
    payload: error,
  };
}

export function duplicateUserSegment(originalId, userSegment) {
  return (dispatch, getState) => {
    dispatch(duplicateUserSegmentRequest(userSegment));

    const { name, tags, organization_ids, group_ids, user_type } = userSegment;

    return post(
      `${USER_SEGMENTS_URL}?multibrand=true`,
      {
        user_segment: {
          name,
          tags,
          organization_ids,
          group_ids,
          user_type,
        },
      },
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(({ user_segment }) => {
        dispatch(duplicateUserSegmentSuccess(originalId, user_segment));
        dispatch(
          showSuccessNotification(
            t("knowledge-user-segments.notifications.duplicate.success")
          )
        );
      })
      .catch(err => {
        dispatch(duplicateUserSegmentFailure(err));
        dispatch(
          showErrorNotification(
            t("knowledge-user-segments.notifications.duplicate.failure")
          )
        );
        throw err;
      });
  };
}

function deleteUserSegmentRequest(userSegmentId) {
  return {
    type: types.DELETE_USER_SEGMENT_REQUEST,
    userSegmentId,
  };
}

function deleteUserSegmentSuccess(userSegmentId) {
  return {
    type: types.DELETE_USER_SEGMENT_SUCCESS,
    userSegmentId,
  };
}

function deleteUserSegmentFailure(error) {
  return {
    type: types.DELETE_USER_SEGMENT_FAILURE,
    error: true,
    payload: error,
  };
}

export function deleteUserSegment(userSegmentId) {
  return (dispatch, getState) => {
    dispatch(deleteUserSegmentRequest(userSegmentId));

    return del(`${USER_SEGMENTS_URL}/${userSegmentId}?multibrand=true`, {
      csrfToken: getCSRFToken(),
    })
      .then(() => {
        dispatch(deleteUserSegmentSuccess(userSegmentId));
        dispatch(
          showSuccessNotification(
            t("knowledge-user-segments.notifications.delete.success")
          )
        );
      })
      .catch(err => {
        dispatch(deleteUserSegmentFailure(err));
        dispatch(
          showErrorNotification(
            t("knowledge-user-segments.notifications.delete.failure")
          )
        );
        throw err;
      });
  };
}

export function editUserSegment(userSegmentId) {
  return push({
    pathname: `/knowledge/user_segments/${userSegmentId}`,
    search: `?brand_id=${getCurrentBrandId()}`,
  });
}

export function addUserSegment() {
  return push({
    pathname: "/knowledge/user_segments/new",
    search: `?brand_id=${getCurrentBrandId()}`,
  });
}

function fetchUserSegmentRequest() {
  return {
    type: types.FETCH_USER_SEGMENT_REQUEST,
  };
}

function fetchUserSegmentSuccess(userSegment) {
  return {
    type: types.FETCH_USER_SEGMENT_SUCCESS,
    userSegment,
  };
}

function fetchUserSegmentFailure(error) {
  return {
    type: types.FETCH_USER_SEGMENT_FAILURE,
    error: true,
    payload: error,
  };
}

export function fetchUserSegment(userSegmentId) {
  return dispatch => {
    dispatch(fetchUserSegmentRequest());
    return get(`${USER_SEGMENTS_URL}/${userSegmentId}?multibrand=true`)
      .then(({ user_segment: userSegment }) => {
        dispatch(fetchUserSegmentSuccess(userSegment));
        return userSegment;
      })
      .catch(err => {
        dispatch(fetchUserSegmentFailure(err));
        throw err;
      })
      .then(userSegment => {
        if (userSegment.user_type === SIGNED_IN_USERS) {
          return dispatch(
            fetchUserSegmentOrganizations(userSegment.organization_ids)
          );
        } else if (userSegment.user_type === STAFF) {
          return dispatch(fetchUserSegmentGroups(userSegment.group_ids));
        }
      })
      .then(() => dispatch(fetchUsers()));
  };
}

function fetchUserSegmentOrganizationsRequest() {
  return {
    type: types.FETCH_USER_SEGMENT_ORGANIZATIONS_REQUEST,
  };
}

function fetchUserSegmentOrganizationsSuccess(requestedIds, organizations) {
  return {
    type: types.FETCH_USER_SEGMENT_ORGANIZATIONS_SUCCESS,
    requestedIds,
    organizations,
  };
}

function fetchUserSegmentOrganizationsFailure(error) {
  return {
    type: types.FETCH_USER_SEGMENT_ORGANIZATIONS_FAILURE,
    error: true,
    payload: error,
  };
}

function fetchUserSegmentOrganizations(organizationIds) {
  return dispatch => {
    dispatch(fetchUserSegmentOrganizationsRequest());

    if (organizationIds.length === 0) {
      dispatch(fetchUserSegmentOrganizationsSuccess([]));
      return;
    }

    return get(
      `/api/v2/organizations/show_many.json?ids=${organizationIds.join(",")}`
    )
      .then(({ organizations }) =>
        dispatch(
          fetchUserSegmentOrganizationsSuccess(organizationIds, organizations)
        )
      )
      .catch(err => {
        dispatch(fetchUserSegmentOrganizationsFailure(err));
      });
  };
}

function fetchUserSegmentGroupsRequest() {
  return {
    type: types.FETCH_USER_SEGMENT_GROUPS_REQUEST,
  };
}

function fetchUserSegmentGroupsSuccess(groups) {
  return {
    type: types.FETCH_USER_SEGMENT_GROUPS_SUCCESS,
    groups,
  };
}

function fetchUserSegmentGroupsFailure(error) {
  return {
    type: types.FETCH_USER_SEGMENT_GROUPS_FAILURE,
    error: true,
    payload: error,
  };
}

function fetchUserSegmentGroups(groupIds) {
  return dispatch => {
    dispatch(fetchUserSegmentGroupsRequest());

    if (groupIds.length === 0) {
      dispatch(fetchUserSegmentGroupsSuccess([]));
    }

    const promises = groupIds.map(groupId =>
      get(`/api/v2/groups/${groupId}.json`)
    );

    return Promise.all(promises)
      .then(wrappedGroups => {
        const groups = wrappedGroups.map(group => group.group);
        dispatch(fetchUserSegmentGroupsSuccess(groups));
      })
      .catch(err => {
        dispatch(fetchUserSegmentGroupsFailure(err));
        throw err;
      });
  };
}

export function changeName(name) {
  return {
    type: types.CHANGE_NAME,
    name,
  };
}

export function changeUserType(userType) {
  return {
    type: types.CHANGE_USER_TYPE,
    userType,
  };
}

export function changeTag(originalTag, newTag) {
  return dispatch => {
    dispatch({
      type: types.CHANGE_TAG,
      originalTag,
      newTag,
    });

    return dispatch(fetchUsers());
  };
}

export function addTag(tag) {
  return dispatch => {
    dispatch({
      type: types.ADD_TAG,
      tag,
    });

    return dispatch(fetchUsers());
  };
}

export function removeTag(tag) {
  return dispatch => {
    dispatch({
      type: types.REMOVE_TAG,
      tag,
    });

    return dispatch(fetchUsers());
  };
}

export function changeOrganization(originalOrganization, newOrganization) {
  return dispatch => {
    dispatch({
      type: types.CHANGE_ORGANIZATION,
      originalOrganization,
      newOrganization,
    });

    return dispatch(fetchUsers());
  };
}

export function addOrganization(organization) {
  return dispatch => {
    dispatch({
      type: types.ADD_ORGANIZATION,
      organization,
    });

    return dispatch(fetchUsers());
  };
}

export function removeOrganization(organization) {
  return dispatch => {
    dispatch({
      type: types.REMOVE_ORGANIZATION,
      organization,
    });

    return dispatch(fetchUsers());
  };
}

export function changeGroup(originalGroup, newGroup) {
  return dispatch => {
    dispatch({
      type: types.CHANGE_GROUP,
      originalGroup,
      newGroup,
    });

    return dispatch(fetchUsers());
  };
}

export function addGroup(group) {
  return dispatch => {
    dispatch({
      type: types.ADD_GROUP,
      group,
    });

    return dispatch(fetchUsers());
  };
}

export function removeGroup(group) {
  return dispatch => {
    dispatch({
      type: types.REMOVE_GROUP,
      group,
    });

    return dispatch(fetchUsers());
  };
}

function saveUserSegmentRequest() {
  return {
    type: types.SAVE_USER_SEGMENT_REQUEST,
  };
}

function saveUserSegmentSuccess() {
  return {
    type: types.SAVE_USER_SEGMENT_SUCCESS,
  };
}

function saveUserSegmentFailure(error) {
  return {
    type: types.SAVE_USER_SEGMENT_FAILURE,
    error: true,
    payload: error,
  };
}

export function saveUserSegment(userSegmentId) {
  return (dispatch, getState) => {
    const {
      userSegment: { name, tags, groups, organizations },
    } = getState();

    if (organizations.some(organization => organization.deleted)) {
      dispatch(
        showErrorNotification(
          "User segment cannot be saved with deleted organizations"
        )
      );
      return;
    }

    if (groups.some(group => group.deleted)) {
      dispatch(
        showErrorNotification(
          "User segment cannot be saved with deleted groups"
        )
      );
      return;
    }

    dispatch(saveUserSegmentRequest());

    return put(
      `${USER_SEGMENTS_URL}/${userSegmentId}?multibrand=true`,
      {
        user_segment: {
          name,
          tags: tags,
          group_ids: groups.map(({ id }) => id),
          organization_ids: organizations.map(({ id }) => id),
        },
      },
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(() => {
        dispatch(saveUserSegmentSuccess());
        dispatch(
          showSuccessNotification(
            t("knowledge-user-segments.notifications.save.success")
          )
        );
      })
      .catch(err => {
        dispatch(saveUserSegmentFailure(err));
        dispatch(
          showErrorNotification(
            t("knowledge-user-segments.notifications.save.failure")
          )
        );
        throw err;
      });
  };
}

function createUserSegmentRequest() {
  return {
    type: types.CREATE_USER_SEGMENT_REQUEST,
  };
}

function createUserSegmentSuccess() {
  return {
    type: types.CREATE_USER_SEGMENT_SUCCESS,
  };
}

function createUserSegmentFailure(error) {
  return {
    type: types.CREATE_USER_SEGMENT_FAILURE,
    error: true,
    payload: error,
  };
}

export function createUserSegment() {
  return (dispatch, getState) => {
    dispatch(createUserSegmentRequest());

    const {
      userSegment: { name, tags, groups, organizations, userType },
    } = getState();

    return post(
      `${USER_SEGMENTS_URL}?multibrand=true`,
      {
        user_segment: {
          name,
          user_type: userType,
          tags: tags,
          group_ids: groups.map(({ id }) => id),
          organization_ids: organizations.map(({ id }) => id),
        },
      },
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(({ user_segment: { id } }) => {
        dispatch(createUserSegmentSuccess());
        dispatch(
          showSuccessNotification(
            t("knowledge-user-segments.notifications.create.success")
          )
        );
        dispatch(
          push({
            pathname: `/knowledge/user_segments/${id}`,
            search: `?brand_id=${getCurrentBrandId()}`,
          })
        );
      })
      .catch(err => {
        dispatch(createUserSegmentFailure(err));
        dispatch(
          showErrorNotification(
            t("knowledge-user-segments.notifications.create.failure")
          )
        );
        throw err;
      });
  };
}

function fetchUsersRequest() {
  return {
    type: types.FETCH_USERS_REQUEST,
  };
}

function fetchUsersSuccess(users, count, pageCount) {
  return {
    type: types.FETCH_USERS_SUCCESS,
    users,
    count,
    pageCount,
  };
}

function fetchUsersFailure(error) {
  return {
    type: types.FETCH_USERS_FAILURE,
    error: true,
    payload: error,
  };
}

export function fetchUsers(page = 1) {
  return (dispatch, getState) => {
    const {
      userSegment: { userType, tags, groups, organizations },
    } = getState();

    // Non-admins are not allowed to see user list
    if (!hasEntitlement("admin")) {
      return;
    }

    dispatch(fetchUsersRequest());

    return get(`${USER_SEGMENTS_URL}/users.json`, {
      query: {
        page,
        user_type: userType,
        tags: tags.join(","),
        group_ids: groups.map(g => g.id).join(","),
        organization_ids: organizations.map(o => o.id).join(","),
        per_page: USERS_PER_PAGE,
        multibrand: true,
      },
    })
      .then(({ users, count, page_count }) =>
        dispatch(fetchUsersSuccess(users, count, page_count))
      )
      .catch(err => {
        dispatch(fetchUsersFailure(err));
        throw err;
      });
  };
}

export function resetUserSegment() {
  return {
    type: types.RESET_USER_SEGMENT,
  };
}

export function resetUserSegmentAttributes() {
  return {
    type: types.RESET_USER_SEGMENT_ATTRIBUTES,
  };
}

export function cancelNewUserSegment() {
  return push({
    pathname: "/knowledge/user_segments",
    search: `?brand_id=${getCurrentBrandId()}`,
  });
}

export function filterUserSegments(id) {
  return push({
    pathname: `/knowledge/user_segments/${id}`,
    search: `?brand_id=${getCurrentBrandId()}`,
  });
}



// WEBPACK FOOTER //
// ./src/actions/userSegment/index.js